import * as Sentry from '@sentry/react';

const SENSITIVE_HEADER_KEYS = ['authorization', 'cookie', 'set-cookie', 'x-api-key', 'x-auth-token', 'api-key'];
const SENSITIVE_FIELD_PATTERNS = ['password', 'token', 'secret', 'api_key', 'apikey', 'authorization', 'cookie', 'webhook_secret'];
const HEALTH_PATHS = ['/api/health', '/api/health/ready'];

function isSensitiveKey(k) {
  const lk = String(k || '').toLowerCase();
  if (SENSITIVE_HEADER_KEYS.includes(lk)) return true;
  return SENSITIVE_FIELD_PATTERNS.some((p) => lk.includes(p));
}

function scrubDict(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  Object.keys(obj).forEach((k) => {
    if (isSensitiveKey(k)) {
      obj[k] = '[REDACTED]';
    }
  });
  return obj;
}

function detectEnv() {
  if (process.env.REACT_APP_SENTRY_ENVIRONMENT) return process.env.REACT_APP_SENTRY_ENVIRONMENT;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname || '';
    if (h.includes('1in2000club.com')) return 'production';
    if (h.includes('emergent.host')) return 'production';
    if (h.includes('preview.emergentagent.com')) return 'preview';
    if (h === 'localhost' || h === '127.0.0.1') return 'development';
  }
  return 'production';
}

function beforeSendHandler(event) {
  try {
    // Strip email PII
    if (event.user) {
      delete event.user.email;
      delete event.user.username;
      delete event.user.ip_address;
    }

    // Scrub request
    if (event.request) {
      if (event.request.headers) scrubDict(event.request.headers);
      if (event.request.cookies) {
        Object.keys(event.request.cookies).forEach((k) => {
          event.request.cookies[k] = '[REDACTED]';
        });
      }
      if (event.request.data && typeof event.request.data === 'object') scrubDict(event.request.data);
      if (event.request.query_string && typeof event.request.query_string === 'string') {
        // Strip sensitive query params
        try {
          const params = new URLSearchParams(event.request.query_string);
          let mutated = false;
          for (const key of Array.from(params.keys())) {
            if (isSensitiveKey(key)) {
              params.set(key, '[REDACTED]');
              mutated = true;
            }
          }
          if (mutated) event.request.query_string = params.toString();
        } catch (_e) { /* ignore */ }
      }
      // Ignore benign network failures from health endpoints
      const url = event.request.url || '';
      const msg = (event.message || '') + ' ' + ((event.exception && event.exception.values && event.exception.values[0] && event.exception.values[0].value) || '');
      if (HEALTH_PATHS.some((p) => url.includes(p)) &&
          /Network|Failed to fetch|AbortError|NetworkError/i.test(msg)) {
        return null;
      }
    }

    // Scrub extras and tags
    if (event.extra) scrubDict(event.extra);
    if (event.tags) scrubDict(event.tags);

    // Scrub breadcrumb data
    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs.forEach((bc) => {
        if (bc && bc.data && typeof bc.data === 'object') scrubDict(bc.data);
      });
    }
  } catch (_e) {
    // Never break Sentry pipeline due to scrubbing
  }
  return event;
}

function beforeSendTransactionHandler(event) {
  const txn = event.transaction || '';
  if (HEALTH_PATHS.some((p) => txn.includes(p))) return null;
  return event;
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return false;
  try {
    Sentry.init({
      dsn,
      environment: detectEnv(),
      release: process.env.REACT_APP_SENTRY_RELEASE || '1in2000club-frontend',
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.25,
      sendDefaultPii: false,
      // Explicit integrations: keep default breadcrumbs/HTTP tracking but disable
      // any feature that would consume the fetch response body. This prevents
      // the "body stream already used" error in admin / partner panels.
      integrations: (defaults) => defaults.filter(
        (i) => i && i.name !== 'HttpClient' && i.name !== 'CaptureConsole',
      ),
      // Disable network breadcrumb body capture (was reading response streams).
      maxBreadcrumbs: 50,
      beforeBreadcrumb: (breadcrumb) => {
        if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
          if (breadcrumb.data) {
            // Drop any body capture that might consume streams
            delete breadcrumb.data.response_body_size;
            delete breadcrumb.data.request_body_size;
          }
        }
        return breadcrumb;
      },
      beforeSend: beforeSendHandler,
      beforeSendTransaction: beforeSendTransactionHandler,
    });
    return true;
  } catch (_e) {
    return false;
  }
}

export { Sentry };
